import React, { useEffect, useState, createRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header } from "../../components/Header";

import {
  handleInputChange,
  handlePaste,
} from "../../../../utils/inputHelpers";
import { MailIcon } from "lucide-react";
import authApi from "../../../../api/authApi";
import { consumePostLoginRedirect, persistSessionValue } from "../../../../utils/authStorage";
import { getUserFriendlyErrorMessage } from "../../../../utils/errorMessages";

const EmailVerification = () => {
  const navigate = useNavigate();
  const [resend, setResend] = useState(false);
  const [loading, setLoading] = useState(false)
  const [inputValues, setInputValues] = useState(["", "", "", "", "", ""]);
  const inputRefs = Array(6)
    .fill(null)
    .map(() => createRef<HTMLInputElement>());
  const isButtonEnabled = inputValues.every((val) => val.length === 1);

  const location = useLocation();
  const fromProfile = location.state?.fromProfile || false;
  const new_email = location.state?.new_email || ''
  const verifyPhone = location.state?.verifyPhone || false;
  const new_phone = location.state?.new_phone || ''

  const handleVerifyEmail = async (e: React.MouseEvent<HTMLButtonElement>) => {
    setLoading(true)
    if (fromProfile) {
      await handleVerifyEmailProfile(e);
      setLoading(false)
    } else {
      await handleVerifyEmailSignup(e);
      setLoading(false)
    }
  };

  const handleVerifyEmailSignup = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const code = inputValues.join("");
    const email = sessionStorage.getItem("email");

    if (!email) {
      alert("Missing email.");
      return;
    }

    try {
      const response = await authApi.verifyEmailOtp(email, code);
      if (response.message?.toLowerCase().includes("success")) {
        // const phone = sessionStorage.getItem("phone");
        // if (phone) {
        //   await authApi.sendMobileOtp(phone);
        // }
        // nav to mobile verification
        sessionStorage.removeItem('login_cashflow_refresh_started');
        navigate("/firebase-mobile");
      }
    } catch (error: any) {
      alert(getUserFriendlyErrorMessage(error, "Verification failed. Please try again."));
    }
  };

  const handleVerifyEmailProfile = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const code = inputValues.join("");

    console.log(new_email)
    if (!new_email) {
      alert("Missing email.");
      return;
    }

    try {
      const response = await authApi.verifyEmailOtp(new_email, code);
      if (response.message?.toLowerCase().includes("success")) {
        if (verifyPhone && new_phone) {
          await authApi.sendMobileOtp(new_phone);
          navigate("/mobileverification", { state: { new_phone: new_phone, fromProfile: true } });
        } else {
          persistSessionValue('just_logged_in', 'true');
          sessionStorage.removeItem('login_cashflow_refresh_started');
          navigate(consumePostLoginRedirect() || "/home");
        }
      }
    } catch (error: any) {
      alert(getUserFriendlyErrorMessage(error, "Verification failed. Please try again."));
    }
  };

  const handleResendEmail = async () => {
    let curr_email
    if (fromProfile) {
      curr_email = new_email
    } else {
      curr_email = sessionStorage.getItem("email");
    }

    if (!curr_email) {
      alert("Missing email.");
      return;
    }

    try {
      const response = await authApi.sendEmailOtp(curr_email);
      console.log("OTP sent response:", response?.message);

      setResend(true);
      setTimeout(() => setResend(false), 30000);
    } catch (error) {
      console.error("Email resend failed:", error);
    }
  };

  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />

      <div className="flex flex-col items-center justify-center flex-1 px-4 sm:px-0">
        <div className="w-full max-w-xl bg-white rounded-lg px-6 sm:px-16 py-10 sm:py-12">
          <form className="flex flex-col gap-6">
            {/* Mail Icon */}
            <div className="flex justify-center items-center">
              <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                <MailIcon className="w-6 h-6 text-gray-800" />
              </div>
            </div>

            {/* Headings */}
            <div className="flex flex-col items-center gap-2 text-center">
              <h1 className="text-gray-800 text-2xl sm:text-3xl font-bold">Email verification</h1>
              <p className="text-gray-700 font-medium text-sm sm:text-base">
                Enter the 6-digit verification code sent to your email
              </p>
            </div>

            {/* Input boxes */}
            <div className="flex justify-center gap-2 sm:gap-5">
              {inputRefs.map((inputRef, index) => (
                <input
                  key={index}
                  type="text"
                  ref={inputRef}
                  maxLength={1}
                  value={inputValues[index]}
                  onChange={(e) =>
                    handleInputChange(
                      e,
                      index,
                      inputValues,
                      setInputValues,
                      inputRefs,
                      inputValues.length
                    )
                  }
                  onPaste={(e) =>
                    handlePaste(
                      e,
                      inputValues,
                      setInputValues,
                      inputRefs,
                      inputValues.length
                    )
                  }
                  className="w-12 h-14 sm:w-16 sm:h-20 text-center text-gray-800 text-2xl sm:text-3xl font-medium bg-gray-100 border border-gray-800 rounded-xl focus:border-black focus:outline-black"
                />
              ))}
            </div>

            {/* Resend */}
            {!resend ? (
              <div className="text-center text-sm text-gray-800">
                <span className="underline cursor-pointer" onClick={handleResendEmail}>
                  Resend Code
                </span>
              </div>
            ) : (
              <div className="text-center text-green-700 text-sm">
                Verification code sent. Resend in 30 seconds
              </div>
            )}

            {/* Continue Button */}
            <button
              type="submit"
              className={`w-full button-1 flex items-center justify-center ${isButtonEnabled && !loading
                ? "bg-gray-800 hover:bg-gray-700"
                : "!bg-gray-400 !cursor-not-allowed"
                }`}
              disabled={!isButtonEnabled || loading}
              onClick={handleVerifyEmail}
            >
              {loading ? "Loading..." : "Continue"}
            </button>
          </form>
        </div>
      </div>

      <div className="bg-gray-100" />
    </div>

  );
};

export default EmailVerification;
